import { deriveStyleTokensFromNode } from '@/features/mindmap/core/syntax/Tokens';
import { MINDMAP_VERSION } from './MindmapConstants';
import type { Mindmap, MindmapNode } from './MindmapTypes';
import { getSeedMindmap } from './MindmapState';
import { tryLoadMindmapFromText, TEXT_STORAGE_KEY } from './MindmapTextMigration';
import { exportMindmapToText, importMindmapFromText } from './MindmapText';
import { ensureIndex, loadActiveMindmap, saveActiveMindmap, loadMindmapCollapsed } from './MindmapMulti';

export const STORAGE_KEY = 'mindmap-v2:showcase';

function filterNode(n: MindmapNode): MindmapNode {
  const base: MindmapNode = {
    id: n.id,
    parentId: n.parentId,
    text: n.text,
    order: n.order,
  };
  // Only include optional fields if they have actual values (not undefined/null)
  if (n.collapsed !== undefined) base.collapsed = n.collapsed;
  if (n.textColor) base.textColor = n.textColor;
  if (n.bgColor) base.bgColor = n.bgColor;
  if (n.borderColor) base.borderColor = n.borderColor;
  if (n.shape) base.shape = n.shape;
  if (n.link) base.link = n.link;
  if (n.fontSize) base.fontSize = n.fontSize;
  if (n.lineHeight) base.lineHeight = n.lineHeight;
  if (n.boxShadow) base.boxShadow = n.boxShadow;
  if (n.borderStyle) base.borderStyle = n.borderStyle;
  if (typeof n.borderWidth === 'number') base.borderWidth = n.borderWidth;
  if (n.borderRadius) base.borderRadius = n.borderRadius;
  if (n.styleTokens && n.styleTokens.length > 0) base.styleTokens = [...n.styleTokens];
  const derivedTokens = deriveStyleTokensFromNode(n);
  if (derivedTokens.length > 0) {
    base.styleTokens = derivedTokens;
  }
  return base;
}

function filterMindmap(m: Mindmap): Mindmap {
  return {
    id: m.id,
    version: m.version,
    rootId: m.rootId,
    nodes: m.nodes.map(filterNode),
    layoutDirection: m.layoutDirection,
    meta: m.meta ? { ...m.meta } : undefined,
  };
}

export function loadMindmap(): Mindmap {
  try {
    if (typeof window === 'undefined') throw new Error('window');
    const storage = window.localStorage;

    // Prefer text snapshot for migration from legacy single-key storage
    const textMindmap = tryLoadMindmapFromText(storage);
    if (textMindmap) {
      const filtered = filterMindmap(textMindmap);
      if (!filtered.layoutDirection) filtered.layoutDirection = 'right';
      filtered.nodes = filtered.nodes.map((node) => ({ ...node }));
      // Ensure multi-map index exists, and align the in-memory id to the index.activeId
      try {
        const { index } = ensureIndex(storage);
        if (index.activeId && filtered.id !== index.activeId) {
          filtered.id = index.activeId;
        }
        // Persist to per-map snapshot and remove legacy showcase + placeholder keys
        saveActiveMindmap(filtered, storage);
        // Overlay view-level collapsed state if present
        try {
          const collapsed = loadMindmapCollapsed(filtered.id, storage);
          if (collapsed && Array.isArray(collapsed)) {
            const set = new Set(collapsed);
            filtered.nodes = filtered.nodes.map((n) => (set.has(n.id) ? { ...n, collapsed: true } : { ...n, collapsed: undefined }));
          }
        } catch {}
        try { storage.removeItem(TEXT_STORAGE_KEY); } catch {}
        try { storage.removeItem('mindmap-v2:text:mindmap-text-import'); } catch {}
      } catch {}
      return filtered;
    }

    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) throw new Error('missing');
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || typeof parsed.version !== 'number' || !parsed.mindmap) {
      throw new Error('invalid');
    }
    const m: Mindmap = parsed.mindmap;
    if (!m || !Array.isArray(m.nodes) || !m.rootId) throw new Error('shape');
    const filtered = filterMindmap(m);
    if (!filtered.layoutDirection) filtered.layoutDirection = 'right';
    filtered.nodes = filtered.nodes.map((node) => ({ ...node }));

    try {
      // Ensure multi-map index exists and align id to activeId to prevent stray keys
      try {
        const { index } = ensureIndex(storage);
        if (index.activeId && filtered.id !== index.activeId) {
          filtered.id = index.activeId;
        }
      } catch {}
      // Persist into per-map snapshot and remove legacy keys
      saveActiveMindmap(filtered, storage);
      // Overlay view-level collapsed state if present
      try {
        const collapsed = loadMindmapCollapsed(filtered.id, storage);
        if (collapsed && Array.isArray(collapsed)) {
          const set = new Set(collapsed);
          filtered.nodes = filtered.nodes.map((n) => (set.has(n.id) ? { ...n, collapsed: true } : { ...n, collapsed: undefined }));
        }
      } catch {}
      try { storage.removeItem(TEXT_STORAGE_KEY); } catch {}
      storage.removeItem(STORAGE_KEY);
    } catch {
      // ignore write failure
    }

    return filtered;
  } catch {
    // As a final fallback, try multi-map active entry (which internally calls ensureIndex and generates proper UUID)
    try {
      const storage = window.localStorage;
      const active = loadActiveMindmap(storage);
      if (active) {
        const now = new Date().toISOString();
        const enriched = {
          ...active,
          meta: {
            ...(active.meta ?? {}),
            createdAt: active.meta?.createdAt ?? now,
            updatedAt: active.meta?.updatedAt ?? now,
          },
        } as Mindmap;
        const filtered = filterMindmap(enriched);
        if (!filtered.layoutDirection) filtered.layoutDirection = 'right';
        filtered.nodes = filtered.nodes.map((node) => ({ ...node }));
        return filtered;
      }
    } catch {}

    // Absolute fallback: loadActiveMindmap() should never return null because ensureIndex() 
    // creates a seed with proper UUID. But if it somehow does, throw to trigger re-init.
    throw new Error('Failed to load or initialize mindmap');
  }
}

export function createMindmapSaver(
  delayMs = 300,
  hooks?: { onSchedule?: (delayMs: number) => void; onSaved?: () => void }
) {
  let t: ReturnType<typeof setTimeout> | null = null;
  let last: Mindmap | null = null;

  const save = (m: Mindmap) => {
    last = m;
    if (t) clearTimeout(t);
    try {
      hooks?.onSchedule?.(delayMs);
    } catch {}
    t = setTimeout(() => {
      try {
        const withMeta = { ...last!, meta: { ...(last!.meta ?? {}), updatedAt: new Date().toISOString() } };
        const filtered = filterMindmap(withMeta);
        // Persist only to per-map storage; remove any legacy json key
        window.localStorage.removeItem(STORAGE_KEY);
        // Persist into multi-map storage under the document's own id
        saveActiveMindmap(filtered, window.localStorage);
        try {
          hooks?.onSaved?.();
        } catch {}
      } catch {
        // ignore write errors
      }
    }, delayMs);
  };

  const flush = () => {
    if (t) {
      clearTimeout(t);
      t = null;
    }
    if (last) {
      try {
        const withMeta = { ...last, meta: { ...(last.meta ?? {}), updatedAt: new Date().toISOString() } };
        const filtered = filterMindmap(withMeta);
        window.localStorage.removeItem(STORAGE_KEY);
        // Persist into multi-map storage under the document's own id
        saveActiveMindmap(filtered, window.localStorage);
        try {
          hooks?.onSaved?.();
        } catch {}
      } catch {
        // ignore
      }
    }
  };

  const cancel = () => {
    if (t) clearTimeout(t);
    t = null;
    last = null;
  };

  return { save, flush, cancel };
}

function isValidMindmapDirection(value: unknown): value is Mindmap['layoutDirection'] {
  return value === 'left' || value === 'right' || value === 'up' || value === 'down';
}

function assertIsMindmapNode(value: any): asserts value is MindmapNode {
  if (!value || typeof value !== 'object') throw new Error('invalid-node');
  if (typeof value.id !== 'string' || !value.id.trim()) throw new Error('invalid-node-id');
  if (typeof value.text !== 'string') throw new Error('invalid-node-text');
  if (typeof value.order !== 'number' || Number.isNaN(value.order)) throw new Error('invalid-node-order');
  if (
    'parentId' in value &&
    value.parentId !== null &&
    value.parentId !== undefined &&
    typeof value.parentId !== 'string'
  ) {
    throw new Error('invalid-node-parent');
  }
}

function sanitizeImportedMindmap(raw: any): Mindmap {
  if (!raw || typeof raw !== 'object') throw new Error('invalid-mindmap');

  const rootId = typeof raw.rootId === 'string' && raw.rootId.trim().length ? raw.rootId : undefined;
  if (!rootId) throw new Error('invalid-root');

  if (!Array.isArray(raw.nodes) || raw.nodes.length === 0) throw new Error('invalid-nodes');

  const seenIds = new Set<string>();
  for (const node of raw.nodes) {
    assertIsMindmapNode(node);
    if (seenIds.has(node.id)) throw new Error('duplicate-node');
    seenIds.add(node.id);
  }

  if (!seenIds.has(rootId)) throw new Error('missing-root-node');

  const candidate: Mindmap = {
    id: typeof raw.id === 'string' && raw.id.trim().length ? raw.id : `imported-${Date.now()}`,
    version: typeof raw.version === 'number' ? raw.version : MINDMAP_VERSION,
    rootId,
    nodes: raw.nodes as MindmapNode[],
    layoutDirection: isValidMindmapDirection(raw.layoutDirection) ? raw.layoutDirection : 'right',
    meta: raw.meta && typeof raw.meta === 'object' ? { ...raw.meta } : undefined,
  };

  const filtered = filterMindmap(candidate);
  if (!filtered.layoutDirection) filtered.layoutDirection = 'right';
  filtered.version = MINDMAP_VERSION;
  filtered.nodes = filtered.nodes.map((node) => ({ ...node }));
  const now = new Date().toISOString();
  filtered.meta = {
    ...(filtered.meta ?? {}),
    createdAt: filtered.meta?.createdAt ?? now,
    updatedAt: now,
    importedAt: now,
  };

  return filtered;
}

export function parseMindmapImport(content: string): Mindmap {
  const tryJsonImport = () => {
    try {
      const parsed = JSON.parse(content);
      const candidate =
        parsed && typeof parsed === 'object' && 'mindmap' in (parsed as Record<string, unknown>)
          ? (parsed as Record<string, unknown>).mindmap
          : parsed;
      return sanitizeImportedMindmap(candidate);
    } catch {
      return null;
    }
  };

  const jsonResult = tryJsonImport();
  if (jsonResult) return jsonResult;

  try {
    const textMindmap = importMindmapFromText(content);
    return sanitizeImportedMindmap(textMindmap);
  } catch {
    throw new Error('invalid-mindmap-import');
  }
}

export function serializeMindmapForDownload(mindmap: Mindmap): string {
  const withMeta = {
    ...mindmap,
    meta: {
      ...(mindmap.meta ?? {}),
      updatedAt: new Date().toISOString(),
    },
  } as Mindmap;

  const filtered = filterMindmap(withMeta);
  if (!filtered.layoutDirection) filtered.layoutDirection = 'right';
  filtered.version = MINDMAP_VERSION;
  filtered.nodes = filtered.nodes.map((node) => ({ ...node }));

  return exportMindmapToText(filtered);
}
